import { Injectable, signal, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, firstValueFrom, race, timer } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Owns the open/closed state of the mobile overlay menu and exposes a real
 * signal for "overlay close transition has finished" — so callers can sequence
 * their work against layout settling instead of guessing a timeout.
 *
 * Navbar binds to `open` (read/write).
 * NavigationService awaits `waitUntilClosed()` before measuring scroll targets.
 */
@Injectable({ providedIn: 'root' })
export class MobileMenuService {
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Is the mobile overlay menu currently open. */
  readonly open = signal(false);

  /** Fires once each time the overlay finishes closing. */
  private readonly closed$ = new Subject<void>();

  constructor() {
    if (this.isBrowser) {
      // Body scroll-lock tied to overlay state. Lives here so any component
      // that opens/closes the menu gets the lock for free.
      // Uses a one-shot effect-free pattern: toggle in set() wrapper below.
    }
  }

  /** Open or close the overlay. Handles body scroll-lock. */
  setOpen(value: boolean): void {
    this.open.set(value);
    if (this.isBrowser) {
      document.body.style.overflow = value ? 'hidden' : '';
    }
  }

  toggle(): void {
    this.setOpen(!this.open());
  }

  /** Notify that the overlay's close transition has finished painting. */
  notifyClosed(): void {
    this.closed$.next();
  }

  /**
   * Resolves once the overlay is fully closed and the page layout has painted
   * at least one frame since. If the overlay is already closed, resolves on the
   * next microtask. A hard 600ms ceiling guards against missed transitionend
   * events (e.g. reduced-motion mode where no transition fires).
   */
  async waitUntilClosed(): Promise<void> {
    if (!this.open()) return;
    this.setOpen(false);
    await firstValueFrom(
      race(this.closed$.asObservable(), timer(600)).pipe(map(() => void 0)),
    );
  }
}
